import { HttpClient } from '@angular/common/http';
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges, TemplateRef } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Subject, firstValueFrom, forkJoin, of } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FormGroup, FormBuilder, Validators, FormControl } from '@angular/forms';
import { WorkOrderService } from '../work-order-details/work-order.service';
import { AddressInfo, ContactDetails, ContactInfo, LadingResponse, PhoneInfo } from '../work-order-details/work-order.interfaces';
import { DateTime } from 'luxon';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ViewChild } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { FscApiService } from 'src/app/rates/fsc-manager/fsc-api.service';
import { FscRate, isActive } from '../../rates/fsc-manager/fsc.models';

/*
  YOU WANT TO TRACK CHANGES TO isEditing SO YOU'RE ABOUT TO TURN IT INTO A BehaviorSubject<boolean> 
  YOU'LL ALSO WRITE A GETTER, BUT NOT TECHNICALLY A GETTER. LIKE getIsEditing() 
  AND IT WILL RETURN this.isEditing.asObservable() OR SOMETHING

  IT WILL BREAK THE CODE
*/

type SettlementSideKind = 'carrier' | 'customer';
type PartyRoleKey = 'shipper' | 'consignee' | 'customer' | 'carrier';

interface SettlementApiResponse<T> {
  file?: string;
  code?: string | number;
  message?: string;
  data?: T;
}

interface AutorateResponse {
  ok: boolean;
  message?: string;
  can_apply?: boolean;
  preview_hash?: string;
  reasons?: string[];
  database_writes_performed?: boolean;
  ladings?: Array<{
    customer_charges: { is_complete: boolean };
    carrier_costs: { is_complete: boolean };
  }>;
}

interface SettlementSummary {
  amount_cents: number | null;
  percent: number | null;
}

interface SettlementLineItemResponse {
  lading_carrier_cost_line_item_uuid?: string;
  lading_customer_charge_line_item_uuid?: string;
  service_name?: string;
  service_type?: string | null;
  rate_cents?: number | null;
  rate_unit?: string | null;
  multiplier?: number | null;
  net_amount_cents?: number | null;
  charge_mode?: string | null;
  contact_service_rate_uuid?: string | null;
  freight_rate_uuid?: string | null;
  labour_review_uuid?: string | null;
  sort_order?: number | null;
  note?: string | null;
  created_at?: string | null;
  quantity_basis?: string | null;
  raw_quantity?: number | string | null;
  billable_quantity?: number | string | null;
  billable_minutes?: number | string | null;
  display_quantity_unit?: string | null;
  pricing_factor?: number | string | null;
  pricing_context?: string | null;
}

interface SettlementSideResponse {
  lading_carrier_cost_uuid?: string;
  lading_customer_charge_uuid?: string;
  carrier_contact_uuid?: string | null;
  customer_contact_uuid?: string | null;
  subtotal_cents?: number | null;
  tax_rate?: number | null;
  tax_total_cents?: number | null;
  total_cents?: number | null;
  totals_consistent?: boolean;
  totals_need_repair?: boolean;
  line_items?: SettlementLineItemResponse[];
  audited?: boolean | null;
}

interface SettlementResponseData {
  lading_settlement_uuid: string;
  lading_uuid: string;
  settlement_source: string;
  rating_status: string;
  carrier_complete: boolean;
  customer_complete: boolean;
  margin_complete: boolean;
  warnings: string[];
  blocking_errors: string[];
  margin: SettlementSummary;
  created_at: string | null;
  updated_at: string | null;
  carrier_costs: SettlementSideResponse[];
  customer_charges: SettlementSideResponse[];
}

interface EditableLineItem {
  clientId: string;
  persistedUuid: string | null;
  serviceName: string;
  serviceType: string;
  rateDollars: number | null;
  rateUnit: string;
  multiplier: number | null;
  netAmountCents: number | null;
  chargeMode: string;
  contactServiceRateUuid: string | null;
  freightRateUuid: string | null;
  labourReviewUuid: string | null;
  note: string;
  sortOrder: number;
  isNew: boolean;
  quantityBasis: string;
  rawQuantity: number | null;
  billableQuantity: number | null;
  billableMinutes: number | null;
  displayQuantityUnit: string;
  pricingFactor: number | null;
  pricingContext: string;
}

interface EditableInvoiceSide {
  kind: SettlementSideKind;
  headerUuid: string | null;
  contactUuid: string | null;
  taxRate: number | null;
  authoritativeSubtotalCents: number | null;
  authoritativeTaxTotalCents: number | null;
  authoritativeTotalCents: number | null;
  lineItems: EditableLineItem[];
}

interface PartyCardDefinition {
  key: PartyRoleKey;
  label: string;
  iconClass: string;
}

interface PartySummary extends PartyCardDefinition {
  name: string;
  email: string;
  street: string;
  localityRegionPostal: string;
  phone: string;
}

interface ServiceNamesResponse {
  code?: string;
  message?: string[];
}

interface ServiceRateLookupResponse {
  code?: string;
  message?: string;
  rate?: string | number | null;
  contact_service_rate_uuid?: string | null;
  freight_rate_uuid?: string | null;
  rate_unit?: string | null;
}

@Component({
  selector: 'app-new-new-financials',
  templateUrl: './new-new-financials.component.html',
  styleUrls: ['./new-new-financials.component.scss']
})
export class NewNewFinancialsComponent implements OnInit, OnChanges, OnDestroy {
  @Input() hideMenu = false;
  @Input() ladingUuid: string | null = null;

  private readonly settlementLedgerUrl = '/api/work_orders/settlement_ledger.php';
  private financialOperationId: string | null = null;
  private readonly autorateUrl = '/api/work_orders/autorate_v2.php';
  private readonly carrierConfirmationWorkflowUrl = '/api/work_orders/carrier_rate_confirmation_pdf_workflow_v2.php';
  private readonly destroy$ = new Subject<void>();
  private routeLadingUuid: string | null = null;
  private lineItemSequence = 0;
  private loadingForUuid: string | null = null;

  loading = false;
  saving = false;
  recalculating = false;
  autorating = false;
  hasSettlement = false;
  chargesApproved = false;
  errorMessage = '';
  isEditing = new BehaviorSubject<boolean>(false); // Added view toggle

  settlement: SettlementResponseData | null = null;
  carrierSide: EditableInvoiceSide = this.createEmptySide('carrier');
  customerSide: EditableInvoiceSide = this.createEmptySide('customer');

  private originalCarrierSnapshot = '';
  private originalCustomerSnapshot = '';
  private deletedCarrierLineItemIds: string[] = [];
  private deletedCustomerLineItemIds: string[] = [];

  generalInfoForm!: FormGroup;
  private shipperTimezone: string | null = 'America/Toronto';
  private consigneeTimezone: string | null = 'America/Toronto';
  alternate_reference: string = '';
  workOrderDetails: LadingResponse | null = null;
  partySummaries: PartySummary[] = [];
  carrierServiceNames: string[] = [];
  customerServiceNames: string[] = [];
  private serviceNamesLoadedForUuid: string | null = null;
  readonly partyCardDefinitions: PartyCardDefinition[] = [
    { key: 'shipper', label: 'Shipper', iconClass: 'fa-box-open' },
    { key: 'consignee', label: 'Consignee', iconClass: 'fa-house' },
    { key: 'customer', label: 'Customer', iconClass: 'fa-briefcase' },
    { key: 'carrier', label: 'Carrier', iconClass: 'fa-truck' }
  ];

  optionalCCControl: FormControl = new FormControl('');
  optionalBCCControl: FormControl = new FormControl('');
  
  sendCopyToggle: boolean = true;
  pdfToggle: boolean = false;
  @ViewChild('carrierEmailAddressDialog') carrierEmailAddressDialog!: TemplateRef<unknown>;
  @ViewChild('carrierConfirmationPdfPreviewDialog') carrierConfirmationPdfPreviewDialog!: TemplateRef<unknown>;
  carrierEmailAddressControl: FormControl = new FormControl('', [Validators.required, Validators.email]);
  carrierConfirmationPdfPreviewUrl: SafeResourceUrl | null = null;
  carrierConfirmationPdfPreviewLoading = false;
  carrierConfirmationPdfSending = false;
  private carrierConfirmationPdfObjectUrl: string | null = null;
  private carrierConfirmationPdfDialogRef: MatDialogRef<unknown> | null = null;

  customerFscRate: FscRate | null = null;
  carrierFscRate: FscRate | null = null;
  
  constructor(
    private http: HttpClient,
    private activatedRoute: ActivatedRoute,
    private toastr: ToastrService,
    private fb: FormBuilder,
    private workOrderService: WorkOrderService,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer,
    private fscApi: FscApiService
  ) {
    this.initializeLadingInfo();
  }

  ngOnInit(): void {
    this.activatedRoute.paramMap
      .pipe(takeUntil(this.destroy$))
      .subscribe(params => {
        this.routeLadingUuid = params.get('uuid');
        this.loadIfReady();
      });
    this.isEditing.subscribe( val => { 
      console.log(`is editing is being modified to ${val}`);
      if(!val && this.canSave) this.toastr.warning('Remember to save if needed.');
    })
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('ladingUuid' in changes) {
      this.loadIfReady();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initializeLadingInfo(){
    this.generalInfoForm = this.fb.group({
          proBill: [''],
          ladingNumber: [''],
          customerName: [''],
          shipperName: [''],
          consigneeName: [''],
          carrierName: [''],
          alternateReference: [''],
          ladingStatus: [''],
          tripNumber: [''],
          dropTrailerStatus: [''],
          hot: [false],
          expedited: [false],
          dropTrailer: [false],
          partialOrFull: [''],
          ladingId: [''],
          shipmentNumber: [''],
          loadWeight: ['0'],
          loadWeightUnit: [''],
          loadWeightActual: ['0'],
          loadLength: ['0'],
          loadLengthUnit: [''],
          loadPallets: ['0'],
          loadPieces: ['0'],
          typeOfEquipment: [''],
          appointmentDate: [''],
          appointmentStart: [''],
          appointmentEnd: [''],
          appointmentTimezone: [this.consigneeTimezone],
          loadStartDate: [''],
          loadStartTime: ['', Validators.pattern('^([01]\\d|2[0-3]):([0-5]\\d)$')],
          loadEndDate: [''],
          loadEndTime: ['', Validators.pattern('^([01]\\d|2[0-3]):([0-5]\\d)$')],
          loadSpecialInfo: [''],
          ladingUuid: [''],
          carrierTrailer: [''],
          customerPo: [''],
          weekNo: [''],
          created: [''],
          modifiedDate: [''],
          modifiedTime: [''],
          estimatedLoadStart: [''],
          estimatedLoadStartHours: [''],
          estimatedLoadEnd: [''],
          estimatedLoadEndHours: ['']
        });
  }

  get activeLadingUuid(): string | null {
    return this.ladingUuid || this.routeLadingUuid;
  }

  get canSave(): boolean {
    return this.hasSettlement && !this.saving && this.hasUnsavedChanges;
  }

  get hasUnsavedChanges(): boolean {
    return (
      this.snapshotSide(this.carrierSide) !== this.originalCarrierSnapshot ||
      this.snapshotSide(this.customerSide) !== this.originalCustomerSnapshot ||
      this.deletedCarrierLineItemIds.length > 0 ||
      this.deletedCustomerLineItemIds.length > 0
    );
  }

  get carrierTotals() {
    return this.computeSideTotals(this.carrierSide);
  }

  get customerTotals() {
    return this.computeSideTotals(this.customerSide);
  }

  get draftMarginCents(): number | null {
    if (this.carrierTotals.totalCents === null || this.customerTotals.totalCents === null) {
      return null;
    }
    return this.customerTotals.totalCents - this.carrierTotals.totalCents;
  }

  get draftMarginPercent(): number | null {
    if (this.draftMarginCents === null || this.customerTotals.totalCents === null || this.customerTotals.totalCents <= 0) {
      return null;
    }
    return Number(((this.draftMarginCents / this.customerTotals.totalCents) * 100).toFixed(2));
  }

  approveCustomerCharges(){
    // find the settlement uuid
    if (!this.settlement?.lading_settlement_uuid) return;
    const ladingSettlementUuid = this.settlement.lading_settlement_uuid;

    // call the script
    const url = "/api/financials/financials_approval.php";
    const postBody = {
      'lading_settlement_uuid': ladingSettlementUuid,
      'party': 'customer',
      'audited': true
    };
    this.http.post<any>(url, postBody).subscribe(
      res => {
        console.log(res)
        if (res['code'] && res['code'] === 200) this.toastr.success('Customer charges are now approved.');
        else this.toastr.error('Customer charges could not be approved.');
      },
      error => {
        console.error(error);
        this.toastr.error('Customer charges could not be approved.');
      }
    )
  }

  approveCarrierCosts(){
    // find the settlement uuid
    if (!this.settlement?.lading_settlement_uuid) return;
    const ladingSettlementUuid = this.settlement.lading_settlement_uuid;

    // call the script
    const url = "/api/financials/financials_approval.php";
    const postBody = {
      'lading_settlement_uuid': ladingSettlementUuid,
      'party': 'carrier',
      'audited': true
    };
    this.http.post<any>(url, postBody).subscribe(
      res => {
        console.log(res)
        if (res['code'] && res['code'] === 200) this.toastr.success('Carrier costs are now approved.');
        else this.toastr.error(res.message ?? 'Carrier costs could not be approved.');
      },
      error => {
        console.error(error);
        this.toastr.error('Carrier costs could not be approved.');
      }
    )
  }

  async runAutorate(): Promise<void> {
    const uuid = this.activeLadingUuid;
    if (!uuid || this.autorating) {
      return;
    }

    this.autorating = true;
    this.errorMessage = '';

    try {
      const preview = await firstValueFrom(this.http.post<AutorateResponse>(this.autorateUrl, {
        lading_uuid: uuid, dry_run: true,
      }));
      if (!preview.ok || !preview.can_apply || !preview.preview_hash) {
        throw new Error(preview.reasons?.join(' ') || preview.message || 'Financials need review before autorating.');
      }
      const response = await firstValueFrom(this.http.post<AutorateResponse>(this.autorateUrl, {
        lading_uuid: uuid, dry_run: false, preview_hash: preview.preview_hash,
      }, { headers: { 'Idempotency-Key': crypto.randomUUID() } }));
      if (!response.ok || !response.database_writes_performed) {
        throw new Error(response.message || 'Autorate did not save financials.');
      }
      if (response.ladings?.some((leg) => !leg.customer_charges.is_complete || !leg.carrier_costs.is_complete)) {
        this.toastr.warning('Financials saved with missing rates. Review the incomplete lines.');
      } else {
        this.toastr.success('Financials rated');
      }
      await this.loadSettlement(uuid);
    } catch (error: unknown) {
      const message = this.getErrorMessage(error, 'Unable to autorate this lading.');
      this.toastr.error(message);
      this.errorMessage = message;
    } finally {
      this.autorating = false;
    }
  }

  async saveFinancials(): Promise<void> {
    if (!this.settlement?.lading_settlement_uuid || !this.activeLadingUuid || this.saving || !this.hasSettlement) {
      return;
    }

    if (!this.validateDraft()) {
      return;
    }

    this.saving = true;
    this.financialOperationId = crypto.randomUUID();

    try {
      await this.saveSideChanges(this.carrierSide);
      await this.saveSideChanges(this.customerSide);
      await this.persistTaxRateChanges();
      await this.recalculateSettlement(false);
      await this.loadSettlement(this.activeLadingUuid);
      this.isEditing.next(false); // Auto switch back to view mode on successful save
      this.toastr.success('Financials saved');
    } catch (error: unknown) {
      this.toastr.error(this.getErrorMessage(error, 'Unable to save financials.'));
    } finally {
      this.saving = false;
      this.financialOperationId = null;
    }
  }

  async recalculateSettlement(showToast = true): Promise<void> {
    console.log('you have reached the recalculate settlement function');
    if (!this.settlement?.lading_settlement_uuid || this.recalculating) {
      console.log(`is there a settlement id? ${this.settlement?.lading_settlement_uuid}`);
      console.log(`are we already recalculating? ${this.recalculating}`);
      return;
    }

    this.recalculating = true;

    try {
      const response = await this.postLedgerAction({
        action: 'recalculate',
        lading_settlement_uuid: this.settlement.lading_settlement_uuid
      });

      if (String(response?.code ?? '') !== '200') {
        throw new Error(response?.message || 'Recalculation failed');
      }

      if (showToast) {
        this.toastr.success('Totals recalculated');
      }

      if (response?.data) {
        this.settlement = response.data;
        this.hydrateDrafts(response.data);
      } else if (this.activeLadingUuid) {
        await this.loadSettlement(this.activeLadingUuid);
      }
    } catch (error: unknown) {
      if (showToast) {
        this.toastr.error(this.getErrorMessage(error, 'Unable to recalculate totals.'));
      }
      throw error;
    } finally {
      this.recalculating = false;
    }
  }

  addLineItem(side: EditableInvoiceSide): void {
    if (!side.headerUuid) {
      this.toastr.error(`No ${side.kind} settlement header exists yet.`);
      return;
    }

    side.lineItems.push({
      clientId: this.nextLineItemClientId('new'),
      persistedUuid: null,
      serviceName: '',
      serviceType: '',
      rateDollars: null,
      rateUnit: 'FLAT',
      multiplier: 1,
      netAmountCents: null,
      chargeMode: 'flat',
      contactServiceRateUuid: null,
      freightRateUuid: null,
      labourReviewUuid: null,
      note: '',
      sortOrder: side.lineItems.length + 1,
      isNew: true,
      quantityBasis: '',
      rawQuantity: null,
      billableQuantity: null,
      billableMinutes: null,
      displayQuantityUnit: '',
      pricingFactor: null,
      pricingContext: ''
    });
  }

  removeLineItem(side: EditableInvoiceSide, item: EditableLineItem): void {
    side.lineItems = side.lineItems.filter(entry => entry.clientId !== item.clientId);

    if (item.persistedUuid) {
      const deletedList = side.kind === 'carrier'
        ? this.deletedCarrierLineItemIds
        : this.deletedCustomerLineItemIds;

      if (!deletedList.includes(item.persistedUuid)) {
        deletedList.push(item.persistedUuid);
      }
    }

    this.resequenceLineItems(side);
  }

  updateLineText(item: EditableLineItem, field: 'serviceName' | 'serviceType' | 'rateUnit' | 'chargeMode' | 'note', value: string): void {
    item[field] = value;
  }

  updateServiceName(item: EditableLineItem, value: string): void {
    item.serviceName = value;
    item.contactServiceRateUuid = null;
    item.freightRateUuid = null;
  }

  applyDefaultRateForService(sideKind: SettlementSideKind, item: EditableLineItem, rawServiceName: string): void {
    const serviceName = rawServiceName.trim();
    item.serviceName = serviceName;

    if (!serviceName || !this.activeLadingUuid) {
      item.contactServiceRateUuid = null;
      item.freightRateUuid = null;
      return;
    }

    const chargeType = sideKind === 'carrier' ? 1 : 2;
    const url = `/api/work_orders/accessorial_service_rates.php?lading_uuid=${encodeURIComponent(this.activeLadingUuid)}&charge_type=${chargeType}&service=${encodeURIComponent(serviceName)}&action=check_service_rate`;

    this.http.get<ServiceRateLookupResponse>(url).subscribe({
      next: (response) => {
        if (response?.rate !== null && response?.rate !== undefined && response?.rate !== '') {
          const numericRate = Number(response.rate);

          if (Number.isFinite(numericRate)) {
            item.rateDollars = numericRate;
            item.multiplier = item.multiplier ?? 1;
            item.netAmountCents = this.calculateNetAmountCents(item.rateDollars, item.multiplier);
          }

          item.contactServiceRateUuid = response.contact_service_rate_uuid ?? null;
          item.freightRateUuid = response.freight_rate_uuid ?? null;

          if (response.rate_unit) {
            item.rateUnit = response.rate_unit;
          }
        } else {
          item.contactServiceRateUuid = null;
          item.freightRateUuid = null;
        }
      },
      error: () => {
        item.contactServiceRateUuid = null;
        item.freightRateUuid = null;
      }
    });
  }

  updateLineNumber(item: EditableLineItem, field: 'rateDollars' | 'multiplier', value: string | number | null): void {
    item[field] = this.parseNullableNumber(value);
    const calculated = this.calculateNetAmountCents(item.rateDollars, item.multiplier);
    if (calculated !== null) {
      item.netAmountCents = calculated;
    }
  }

  updateTaxRate(side: EditableInvoiceSide, value: string | number | null): void {
    const numericValue = this.parseNullableNumber(value);
    side.taxRate = numericValue === null ? null : Number((numericValue / 100).toFixed(6));
  }

  displayTaxRatePercent(side: EditableInvoiceSide): number | null {
    if (side.taxRate === null || side.taxRate === undefined) {
      return null;
    }
    return Number((side.taxRate * 100).toFixed(4));
  }

  formatMoney(cents: number | null | undefined): string {
    if (cents === null || cents === undefined) {
      return '0.00';
    }
    return (cents / 100).toFixed(2);
  }

  formatLineQuantity(item: EditableLineItem): string {
    const displayUnit = item.displayQuantityUnit.trim().toLowerCase();

    if (displayUnit === 'minutes') {
      const minutes = item.billableMinutes ?? item.billableQuantity ?? item.rawQuantity;
      const quantity = minutes === null
        ? this.formatCompactQuantity(item.multiplier)
        : `${this.formatCompactQuantity(minutes)} min`;
      return this.withPricingFactor(quantity, item.pricingFactor);
    }

    if (displayUnit === 'hours') {
      const hours = item.billableMinutes !== null
        ? item.billableMinutes / 60
        : item.billableQuantity !== null && item.quantityBasis.endsWith('_minutes')
          ? item.billableQuantity / 60
          : item.multiplier;
      const quantity = hours === null ? '-' : `${this.formatCompactQuantity(hours)} hr`;
      return this.withPricingFactor(quantity, item.pricingFactor);
    }

    if (displayUnit === 'people') {
      const people = item.billableQuantity ?? item.rawQuantity ?? item.multiplier;
      if (people === null) return '-';
      return `${this.formatCompactQuantity(people)} ${Math.abs(people - 1) < 0.000001 ? 'person' : 'people'}`;
    }

    if (displayUnit === 'nights') {
      const nights = item.billableQuantity ?? item.rawQuantity ?? item.multiplier;
      if (nights === null) return '-';
      return `${this.formatCompactQuantity(nights)} ${Math.abs(nights - 1) < 0.000001 ? 'night' : 'nights'}`;
    }

    return this.formatCompactQuantity(item.multiplier);
  }

  private withPricingFactor(quantity: string, factor: number | null): string {
    if (quantity === '-' || factor === null || !Number.isFinite(factor) || Math.abs(factor - 1) < 0.000001) {
      return quantity;
    }

    return `${quantity} × ${this.formatCompactQuantity(factor)}`;
  }

  formatLineQuantityTitle(item: EditableLineItem): string | null {
    const factor = item.pricingFactor;
    if (factor === null || !Number.isFinite(factor) || Math.abs(factor - 1) < 0.000001) {
      return null;
    }

    const context = item.pricingContext.trim();
    const contextLabel = context === '' ? '' : ` (${context})`;
    return `Pricing adjustment${contextLabel}: × ${this.formatCompactQuantity(factor)}`;
  }

  private formatCompactQuantity(value: number | null | undefined): string {
    if (value === null || value === undefined || !Number.isFinite(value)) {
      return '-';
    }

    return Number(value.toFixed(2)).toString();
  }

  formatPercent(value: number | null | undefined): string {
    if (value === null || value === undefined) {
      return 'N/A';
    }
    return `${value.toFixed(2)}%`;
  }

  formatTimestamp(value: string | null | undefined): string {
    if (!value) {
      return 'N/A';
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }

    return date.toLocaleString();
  }

  trackLineItem(_: number, item: EditableLineItem): string {
    return item.clientId;
  }

  sourceLabel(source: string | null | undefined): string {
    switch (source) {
      case 'autorater':
        return 'Autorated';
      case 'semi-auto':
        return 'Semi-Auto';
      case 'manual':
        return 'Manual';
      default:
        return source || 'Unrated';
    }
  }

  ratingStatusLabel(status: string | null | undefined): string {
    switch (status) {
      case 'complete':
        return 'Complete';
      case 'partial':
        return 'Partial';
      case 'unrated_values':
        return 'Missing Values';
      default:
        return status || 'Unknown';
    }
  }

  // Updated to return Tailwind Classes
  statusClass(status: string | null | undefined): string {
    switch (status) {
      case 'complete':
        return 'bg-emerald-50 text-emerald-600 border-emerald-200';
      case 'partial':
        return 'bg-amber-50 text-amber-600 border-amber-200';
      case 'unrated_values':
        return 'bg-rose-50 text-rose-600 border-rose-200';
      default:
        return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  }

  // Updated to return Tailwind Classes
  sourceClass(source: string | null | undefined): string {
    switch (source) {
      case 'autorater':
        return 'bg-indigo-50 text-indigo-600 border-indigo-200';
      case 'semi-auto':
        return 'bg-amber-50 text-amber-600 border-amber-200';
      case 'manual':
        return 'bg-slate-100 text-slate-600 border-slate-200';
      default:
        return 'bg-slate-100 text-slate-600 border-slate-200';
    }
  }

  sideHasRows(side: EditableInvoiceSide): boolean {
    return side.lineItems.length > 0;
  }

  private async loadIfReady() {
    const uuid = this.activeLadingUuid;
    if (!uuid || this.loadingForUuid === uuid) {
      return;
    }

    this.customerFscRate = null;
    this.carrierFscRate = null;

    this.loadAvailableServiceNames(uuid);
    void this.loadSettlement(uuid);

    const workOrderDetails$ = this.workOrderService.getWorkOrderDetails(this.activeLadingUuid);
    forkJoin({
      workOrderResponse: workOrderDetails$
    }).subscribe({
      next: ({workOrderResponse}) => {
        if (workOrderResponse.lading) {
          this.workOrderDetails = workOrderResponse;
          this.partySummaries = this.buildPartySummaries(workOrderResponse);
          this.populateTimezones(workOrderResponse);
          this.populateGeneralInfo(workOrderResponse);

          const lading = workOrderResponse.lading;
          const loadStartDate = lading.load_start?.slice(0, 10);

          if (loadStartDate) {
            forkJoin({
              customerRates: lading.customer_contact_uuid
                ? this.fscApi.rates('customer', lading.customer_contact_uuid)
                : of([]),
              carrierRates: lading.carrier_contact_uuid
                ? this.fscApi.rates('carrier', lading.carrier_contact_uuid)
                : of([])
            })
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: ({ customerRates, carrierRates }) => {
                  this.customerFscRate = this.findFscRateActiveOn(customerRates, loadStartDate);
                  this.carrierFscRate = this.findFscRateActiveOn(carrierRates, loadStartDate);
                },
                error: error => {
                  this.customerFscRate = null;
                  this.carrierFscRate = null;
                  console.error('Error loading FSC rates:', error);
                }
              });
          }
        }
      },
      error: (error) => {
        this.workOrderDetails = null;
        this.partySummaries = [];
        console.error('Error loading data: ', error);
      }
    })
  }

  populateTimezones(response: LadingResponse){
    if (response.shipper.address && response.shipper.address.length > 0){
      this.shipperTimezone = response.shipper.address[0].timezone_id || 'America/Toronto';
    }
    else{
      this.shipperTimezone = 'America/Toronto';
    }
    if (response.consignee.address && response.consignee.address.length > 0){
      this.consigneeTimezone = response.consignee.address[0].timezone_id || 'America/Toronto';
      this.generalInfoForm.get('appointmentTimezone')?.setValue(this.consigneeTimezone, { emitEvent: false });
    }
    else{
      this.consigneeTimezone = 'America/Toronto';
      this.generalInfoForm.get('appointmentTimezone')?.setValue(this.consigneeTimezone, { emitEvent: false });
    }
  }

  populateGeneralInfo(response: LadingResponse){
    if (!response.lading) return;

    const lading = response.lading;
    const [modifiedDate, modifiedTime] = lading.modified ? lading.modified.split(' ') : [null, null];

    this.alternate_reference = lading.alternate_reference;

    const displayPickupDate = this.shipperTimezone ? this.convertDbTimeToDisplay(lading.load_start, this.shipperTimezone) : { date: '', time: ''};
    const displayDeliveryDate = this.consigneeTimezone ? this.convertDbTimeToDisplay(lading.load_end, this.consigneeTimezone) : { date: '', time: ''}
    const displayAppointmentStart = lading.appointment_start && this.consigneeTimezone ? this.convertDbTimeToDisplay(lading.appointment_start, this.consigneeTimezone) : { date: '', time: ''} 
    const displayAppointmentEnd = lading.appointment_end && this.consigneeTimezone ? this.convertDbTimeToDisplay(lading.appointment_end, this.consigneeTimezone) : { date: '', time: ''}


    this.generalInfoForm.patchValue({
      ladingUuid: lading.lading_uuid,
      ladingNumber: lading.lading_number,
      proBill: lading.pro_bill,
      customerName: lading.customer_name,
      shipperName: lading.shipper_name,
      consigneeName: lading.consignee_name,
      carrierName: lading.carrier_name,
      ladingStatus: lading.lading_status,
      tripNumber: lading.trip_number,
      alternateReference: lading.alternate_reference,
      partialOrFull: lading.partial_or_full,
      ladingId: lading.lading_id,
      shipmentNumber: lading.shipment_number,
      loadWeight: lading.load_weight?.toString() || '0',
      loadWeightActual: lading.load_weight_actual?.toString() || '0',
      loadWeightUnit: this.normalizeWeightUnit(lading.load_weight_unit),
      loadLength: lading.load_length?.toString() || '0',
      loadLengthUnit: this.normalizeLengthUnit(lading.load_length_unit),
      loadPieces: lading.load_pieces?.toString() || '0',
      loadPallets: lading.load_pallets?.toString() || '0',
      typeOfEquipment: lading.type_of_equipment,
      appointmentDate: displayAppointmentStart.date || '',
      appointmentStart: displayAppointmentStart.time,
      appointmentEnd: displayAppointmentEnd.time,
      appointmentTimezone: lading.appointment_timezone || this.consigneeTimezone,
      loadStartDate: displayPickupDate.date || '',
      loadStartTime: displayPickupDate.time,
      loadEndDate: displayDeliveryDate.date || '',
      loadEndTime: displayDeliveryDate.time,
      loadSpecialInfo: lading.load_special_info,
      carrierTrailer: lading.carrier_trailer,
      customerPo: lading.customer_po,
      weekNo: lading.week_no,
      created: lading.created ? lading.created.split(' ')[0] : '',
      modifiedDate: modifiedDate || '',
      modifiedTime: modifiedTime || '',
      estimatedLoadStart: lading.estimated_load_start ? lading.estimated_load_start.slice(0, 10) : '',
      estimatedLoadStartHours: lading.estimated_load_start_hours ?? '',
      estimatedLoadEnd: lading.estimated_load_end ? lading.estimated_load_end.slice(0, 10) : '',
      estimatedLoadEndHours: lading.estimated_load_end_hours ?? '',
      hot: lading.hot,
      expedited: lading.expedited,
      dropTrailer: lading.drop_trailer,
      dropTrailerStatus: lading.drop_trailer_status,
    }, { emitEvent: false }); 
  }

  private convertDbTimeToDisplay(easternDateTime: string, targetTimezone: string): { date: string; time: string } {
    if (!easternDateTime || !targetTimezone) return { date: '', time: '' };

    try {
      // Account for timestamptz, which puts UTC offset after seconds
      if (easternDateTime.charAt(19) == '-' || easternDateTime.charAt(19) == '+'){
        easternDateTime = easternDateTime.slice(0,19);
      }
      const dateTime = DateTime.fromFormat(easternDateTime, 'yyyy-MM-dd HH:mm:ss', { zone: 'America/Toronto' });
      if (!dateTime.isValid) {
        const dateOnly = easternDateTime.split(' ')[0];
        const fallbackDate = DateTime.fromISO(dateOnly, { zone: 'America/Toronto' });
        if (fallbackDate.isValid) {
          return {
            date: fallbackDate.setZone(targetTimezone).toFormat('yyyy-MM-dd'),
            time: '00:00'
          };
        }
        return { date: '', time: '' };
      }
      return {
        date: dateTime.setZone(targetTimezone).toFormat('yyyy-MM-dd'),
        time: dateTime.setZone(targetTimezone).toFormat('HH:mm')
      };
    } catch (error) {
      console.error('Error converting DB time:', error, { easternDateTime, targetTimezone });
      return { date: '', time: '' };
    }
  }

  private normalizeWeightUnit(value: string | null | undefined): string {
    const normalized = (value || '').trim().toLowerCase();
    if (['lb', 'lbs', 'pound', 'pounds'].includes(normalized)) return 'lb';
    if (['kg', 'kgs', 'kilogram', 'kilograms'].includes(normalized)) return 'kg';
    return normalized;
  }
  
  private normalizeLengthUnit(value: string | null | undefined): string {
    const normalized = (value || '').trim().toLowerCase();
    if (['in', 'inch', 'inches'].includes(normalized)) return 'in';
    if (['ft', 'foot', 'feet'].includes(normalized)) return 'ft';
    if (['cm', 'centimeter', 'centimeters'].includes(normalized)) return 'cm';
    if (['m', 'meter', 'meters'].includes(normalized)) return 'm';
    return normalized;
  }

  private buildPartySummaries(response: LadingResponse): PartySummary[] {
    return this.partyCardDefinitions.map((definition) => this.buildPartySummary(definition, response));
  }

  private buildPartySummary(definition: PartyCardDefinition, response: LadingResponse): PartySummary {
    const contactDetails = this.getPartyContactDetails(response, definition.key);
    const contact = this.getPrimaryContact(contactDetails);
    const address = this.getPrimaryAddress(contactDetails);
    const phone = this.getPrimaryPhone(contactDetails);
    const fallbackName = this.getPartyFallbackField(response, definition.key, 'name') || 'Unassigned';
    const street = [address?.address_street, address?.address_extended]
      .filter((value): value is string => !!value)
      .join(', ') || 'No street provided';
    const locality = address?.address_locality || this.getPartyFallbackField(response, definition.key, 'locality') || 'City';
    const region = address?.address_region || this.getPartyFallbackField(response, definition.key, 'region') || 'State';
    const postalCode = address?.address_postal_code || '';
    const localityRegionPostal = postalCode
      ? `${locality}, ${region} ${postalCode}`.trim()
      : `${locality}, ${region}`;

    return {
      ...definition,
      name: contact?.contact_organization
        || [contact?.contact_name_given, contact?.contact_name_family].filter(Boolean).join(' ').trim()
        || fallbackName,
      email: contact?.contact_email || 'No email provided',
      street,
      localityRegionPostal,
      phone: phone?.phone_number || 'N/A'
    };
  }

  private getPartyContactDetails(response: LadingResponse, role: PartyRoleKey): ContactDetails | undefined {
    return response[role];
  }

  private getPrimaryContact(contactDetails: ContactDetails | undefined): ContactInfo | undefined {
    return contactDetails?.contact?.[0];
  }

  private getPrimaryAddress(contactDetails: ContactDetails | undefined): AddressInfo | undefined {
    return contactDetails?.address?.[0];
  }

  private getPrimaryPhone(contactDetails: ContactDetails | undefined): PhoneInfo | undefined {
    return contactDetails?.phone?.find((entry) => entry.phone_primary) || contactDetails?.phone?.[0];
  }

  private getPartyFallbackField(response: LadingResponse, role: PartyRoleKey, field: 'name' | 'locality' | 'region'): string {
    const lading = response.lading;

    const fallbackMap: Record<PartyRoleKey, { name: string; locality: string; region: string }> = {
      shipper: {
        name: lading.shipper_name || '',
        locality: lading.shipper_locality || '',
        region: lading.shipper_region || ''
      },
      consignee: {
        name: lading.consignee_name || '',
        locality: lading.consignee_locality || '',
        region: lading.consignee_region || ''
      },
      customer: {
        name: lading.customer_name || '',
        locality: lading.customer_locality || '',
        region: lading.customer_region || ''
      },
      carrier: {
        name: lading.carrier_name || '',
        locality: lading.carrier_locality || '',
        region: lading.carrier_region || ''
      }
    };

    return fallbackMap[role][field];
  }

  private loadAvailableServiceNames(ladingUuid: string): void {
    if (this.serviceNamesLoadedForUuid === ladingUuid) {
      return;
    }

    this.serviceNamesLoadedForUuid = ladingUuid;
    this.carrierServiceNames = [];
    this.customerServiceNames = [];

    this.http.get<ServiceNamesResponse>(`/api/rates/service_names.php?lading_uuid=${encodeURIComponent(ladingUuid)}&contact_type=carrier`)
      .subscribe({
        next: (response) => {
          this.carrierServiceNames = response?.code === '200' && Array.isArray(response.message)
            ? response.message
            : [];
        },
        error: () => {
          this.carrierServiceNames = [];
        }
      });

    this.http.get<ServiceNamesResponse>(`/api/rates/service_names.php?lading_uuid=${encodeURIComponent(ladingUuid)}&contact_type=customer`)
      .subscribe({
        next: (response) => {
          this.customerServiceNames = response?.code === '200' && Array.isArray(response.message)
            ? response.message
            : [];
        },
        error: () => {
          this.customerServiceNames = [];
        }
      });
  }

  private async loadSettlement(uuid: string): Promise<void> {
    this.loading = true;
    this.loadingForUuid = uuid;
    this.errorMessage = '';

    try {
      const response = await firstValueFrom(
        this.http.get<SettlementApiResponse<SettlementResponseData>>(
          `${this.settlementLedgerUrl}?lading_uuid=${encodeURIComponent(uuid)}`
        )
      );

      const responseCode = String(response?.code ?? '');

      if (responseCode === '404') {
        this.settlement = null;
        this.hasSettlement = false;
        this.carrierSide = this.createEmptySide('carrier');
        this.customerSide = this.createEmptySide('customer');
        this.resetSnapshots();
        return;
      }

      if (responseCode !== '200' || !response?.data) {
        throw new Error(response?.message || 'Unable to load settlement.');
      }

      this.settlement = response.data;
      this.hasSettlement = true;
      if (response.data?.customer_charges[0]?.audited === true)
        this.chargesApproved = true;
      this.hydrateDrafts(response.data);
    } catch (error: unknown) {
      this.hasSettlement = false;
      this.settlement = null;
      this.errorMessage = this.getErrorMessage(error, 'Unable to load settlement.');
    } finally {
      this.loading = false;
      this.loadingForUuid = null;
    }
  }

  // hydration: taking raw data and turning it into a fully-populated object/form/state/etc.
  private hydrateDrafts(settlement: SettlementResponseData): void {
    this.carrierSide = this.normalizeSide('carrier', settlement.carrier_costs?.[0]);
    this.customerSide = this.normalizeSide('customer', settlement.customer_charges?.[0]);
    this.deletedCarrierLineItemIds = [];
    this.deletedCustomerLineItemIds = [];
    this.originalCarrierSnapshot = this.snapshotSide(this.carrierSide);
    this.originalCustomerSnapshot = this.snapshotSide(this.customerSide);
  }

  private normalizeSide(kind: SettlementSideKind, rawSide?: SettlementSideResponse): EditableInvoiceSide {
    if (!rawSide) {
      return this.createEmptySide(kind);
    }

    const headerUuid = kind === 'carrier'
      ? rawSide.lading_carrier_cost_uuid || null
      : rawSide.lading_customer_charge_uuid || null;

    const contactUuid = kind === 'carrier'
      ? rawSide.carrier_contact_uuid || null
      : rawSide.customer_contact_uuid || null;

    const lineItems = (rawSide.line_items || []).map(rawItem => this.normalizeLineItem(kind, rawItem));

    return {
      kind,
      headerUuid,
      contactUuid,
      taxRate: rawSide.tax_rate ?? null,
      authoritativeSubtotalCents: rawSide.subtotal_cents ?? null,
      authoritativeTaxTotalCents: rawSide.tax_total_cents ?? null,
      authoritativeTotalCents: rawSide.total_cents ?? null,
      lineItems
    };
  }

  private normalizeLineItem(kind: SettlementSideKind, rawItem: SettlementLineItemResponse): EditableLineItem {
    const persistedUuid = kind === 'carrier'
      ? rawItem.lading_carrier_cost_line_item_uuid || null
      : rawItem.lading_customer_charge_line_item_uuid || null;

    return {
      clientId: this.nextLineItemClientId(kind),
      persistedUuid,
      serviceName: rawItem.service_name || '',
      serviceType: rawItem.service_type || '',
      rateDollars: rawItem.rate_cents === null || rawItem.rate_cents === undefined
        ? null
        : Number((rawItem.rate_cents / 100).toFixed(2)),
      rateUnit: rawItem.rate_unit || '',
      multiplier: rawItem.multiplier ?? null,
      netAmountCents: rawItem.net_amount_cents ?? null,
      chargeMode: rawItem.charge_mode || '',
      contactServiceRateUuid: rawItem.contact_service_rate_uuid || null,
      freightRateUuid: rawItem.freight_rate_uuid || null,
      labourReviewUuid: rawItem.labour_review_uuid || null,
      note: rawItem.note || '',
      sortOrder: rawItem.sort_order || 0,
      isNew: false,
      quantityBasis: rawItem.quantity_basis || '',
      rawQuantity: this.parseNullableNumber(rawItem.raw_quantity ?? null),
      billableQuantity: this.parseNullableNumber(rawItem.billable_quantity ?? null),
      billableMinutes: this.parseNullableNumber(rawItem.billable_minutes ?? null),
      displayQuantityUnit: rawItem.display_quantity_unit || '',
      pricingFactor: this.parseNullableNumber(rawItem.pricing_factor ?? null),
      pricingContext: rawItem.pricing_context || ''
    };
  }

  private createEmptySide(kind: SettlementSideKind): EditableInvoiceSide {
    return {
      kind,
      headerUuid: null,
      contactUuid: null,
      taxRate: null,
      authoritativeSubtotalCents: null,
      authoritativeTaxTotalCents: null,
      authoritativeTotalCents: null,
      lineItems: []
    };
  }

  private computeSideTotals(side: EditableInvoiceSide): { subtotalCents: number | null; taxTotalCents: number | null; totalCents: number | null } {
    if (!this.isEditing.value && side.authoritativeSubtotalCents !== null && side.authoritativeTotalCents !== null) {
      return {
        subtotalCents: side.authoritativeSubtotalCents,
        taxTotalCents: side.authoritativeTaxTotalCents,
        totalCents: side.authoritativeTotalCents
      };
    }

    const lineCents = side.lineItems
      .map(item => this.effectiveNetAmountCents(item))
      .filter((value): value is number => value !== null);

    if (lineCents.length === 0) {
      return { subtotalCents: 0, taxTotalCents: side.taxRate ? 0 : null, totalCents: 0 };
    }

    const subtotalCents = lineCents.reduce((sum, value) => sum + value, 0);
    const taxTotalCents = side.taxRate !== null
      ? Math.round(subtotalCents * side.taxRate)
      : null;

    return {
      subtotalCents,
      taxTotalCents,
      totalCents: subtotalCents + (taxTotalCents || 0)
    };
  }

  private calculateNetAmountCents(rateDollars: number | null, multiplier: number | null): number | null {
    if (rateDollars === null || multiplier === null) {
      return null;
    }

    return Math.round(rateDollars * multiplier * 100);
  }

  private effectiveNetAmountCents(item: EditableLineItem): number | null {
    return this.calculateNetAmountCents(item.rateDollars, item.multiplier) ?? item.netAmountCents;
  }

  private parseNullableNumber(value: string | number | null): number | null {
    if (value === null || value === '' || value === undefined) {
      return null;
    }

    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
  }

  private snapshotSide(side: EditableInvoiceSide): string {
    return JSON.stringify({
      kind: side.kind,
      headerUuid: side.headerUuid,
      taxRate: side.taxRate,
      lineItems: side.lineItems.map((item, index) => ({
        persistedUuid: item.persistedUuid,
        serviceName: item.serviceName,
        serviceType: item.serviceType,
        rateDollars: item.rateDollars,
        rateUnit: item.rateUnit,
        multiplier: item.multiplier,
        netAmountCents: this.effectiveNetAmountCents(item),
        contactServiceRateUuid: item.contactServiceRateUuid,
        freightRateUuid: item.freightRateUuid,
        chargeMode: item.chargeMode,
        note: item.note,
        sortOrder: index + 1
      }))
    });
  }

  private resetSnapshots(): void {
    this.deletedCarrierLineItemIds = [];
    this.deletedCustomerLineItemIds = [];
    this.originalCarrierSnapshot = this.snapshotSide(this.carrierSide);
    this.originalCustomerSnapshot = this.snapshotSide(this.customerSide);
  }

  private nextLineItemClientId(prefix: string): string {
    this.lineItemSequence += 1;
    return `${prefix}-${this.lineItemSequence}`;
  }

  private resequenceLineItems(side: EditableInvoiceSide): void {
    side.lineItems.forEach((item, index) => {
      item.sortOrder = index + 1;
    });
  }

  private validateDraft(): boolean {
    for (const side of [this.carrierSide, this.customerSide]) {
      for (const item of side.lineItems) {
        if (!item.serviceName.trim()) {
          this.toastr.error(`Every ${side.kind} line item needs a service name.`);
          return false;
        }
      }
    }

    return true;
  }

  private async saveSideChanges(side: EditableInvoiceSide): Promise<void> {
    this.resequenceLineItems(side);

    const deletedIds = side.kind === 'carrier'
      ? [...this.deletedCarrierLineItemIds]
      : [...this.deletedCustomerLineItemIds];

    for (const deletedId of deletedIds) {
      const response = await this.postLedgerAction({
        action: 'delete_line_item',
        side: side.kind,
        line_item_uuid: deletedId
      });

      if (String(response?.code ?? '') !== '200') {
        throw new Error(response?.message || `Unable to delete ${side.kind} line item.`);
      }
    }

    for (const item of side.lineItems) {
      const payload = {
        side: side.kind,
        service_name: item.serviceName.trim(),
        service_type: item.serviceType.trim() || null,
        rate_cents: item.rateDollars === null ? null : Math.round(item.rateDollars * 100),
        rate_unit: item.rateUnit.trim() || null,
        multiplier: item.multiplier,
        net_amount_cents: this.effectiveNetAmountCents(item),
        contact_service_rate_uuid: item.contactServiceRateUuid,
        freight_rate_uuid: item.freightRateUuid,
        charge_mode: item.chargeMode.trim() || null,
        sort_order: item.sortOrder,
        note: item.note.trim() || null
      };

      if (item.persistedUuid) {
        const response = await this.postLedgerAction({
          action: 'update_line_item',
          line_item_uuid: item.persistedUuid,
          ...payload
        });

        if (String(response?.code ?? '') !== '200') {
          throw new Error(response?.message || `Unable to update ${side.kind} line item.`);
        }
      } else {
        const parentKey = side.kind === 'carrier'
          ? 'lading_carrier_cost_uuid'
          : 'lading_customer_charge_uuid';

        const response = await this.postLedgerAction({
          action: 'add_line_item',
          [parentKey]: side.headerUuid,
          ...payload
        });

        if (String(response?.code ?? '') !== '200') {
          throw new Error(response?.message || `Unable to add ${side.kind} line item.`);
        }
      }
    }
  }

  private async persistTaxRateChanges(): Promise<void> {
    const tasks: Promise<void>[] = [];

    if (this.carrierSide.headerUuid && this.sideTaxRateChanged(this.carrierSide, this.originalCarrierSnapshot)) {
      tasks.push(this.updateSideTaxRate(this.carrierSide));
    }

    if (this.customerSide.headerUuid && this.sideTaxRateChanged(this.customerSide, this.originalCustomerSnapshot)) {
      tasks.push(this.updateSideTaxRate(this.customerSide));
    }

    await Promise.all(tasks);
  }

  private sideTaxRateChanged(side: EditableInvoiceSide, snapshot: string): boolean {
    try {
      const parsed = JSON.parse(snapshot);
      return parsed.taxRate !== side.taxRate;
    } catch {
      return false;
    }
  }

  private async updateSideTaxRate(side: EditableInvoiceSide): Promise<void> {
    const action = side.kind === 'carrier'
      ? 'update_carrier_cost'
      : 'update_customer_charge';

    const uuidKey = side.kind === 'carrier'
      ? 'lading_carrier_cost_uuid'
      : 'lading_customer_charge_uuid';

    const response = await this.postLedgerAction({
      action,
      [uuidKey]: side.headerUuid,
      tax_rate: side.taxRate
    });

    if (String(response?.code ?? '') !== '200') {
      throw new Error(response?.message || `Unable to update ${side.kind} tax rate.`);
    }
  }

  private async postLedgerAction(body: Record<string, unknown>): Promise<SettlementApiResponse<SettlementResponseData>> {
    return await firstValueFrom(
      this.http.post<SettlementApiResponse<SettlementResponseData>>(this.settlementLedgerUrl, body,
        this.financialOperationId ? { headers: { 'X-AMS-Operation-ID': this.financialOperationId } } : {})
    );
  }

  private getErrorMessage(error: unknown, fallback: string): string {
    if (error instanceof Error && error.message) {
      return error.message;
    }

    return fallback;
  }

  private parseEmailList(emailString: string | null): string[] {
    // Simple regex for basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!emailString) return [];
    
    return emailString.split(',')
                      .map(email => email.trim())
                      .filter(email => emailRegex.test(email)); 
  }

  private buildCarrierConfirmationPostBody(send_as_pdf: boolean, extras: Record<string, unknown> = {}): Record<string, unknown> {
    return {
      'lading_uuid': this.generalInfoForm.get('ladingUuid')?.value,
      'send_copy_to_user': this.sendCopyToggle,
      'optional_cc_emails': this.parseEmailList(this.optionalCCControl.value),
      'optional_bcc_emails': this.parseEmailList(this.optionalBCCControl.value),
      'send_as_pdf': send_as_pdf,
      ...extras
    };
  }

  async previewCarrierConfirmationPdf(): Promise<void> {
    if (!this.generalInfoForm.get('ladingUuid')?.value) {
      this.toastr.error('Cannot preview carrier confirmation without a lading UUID.');
      return;
    }

    this.carrierConfirmationPdfPreviewLoading = true;
    this.clearCarrierConfirmationPdfPreview();

    const postBody = this.buildCarrierConfirmationPostBody(true, { see_preview: true });

    this.http.post(this.carrierConfirmationWorkflowUrl, postBody, {
      observe: 'response',
      responseType: 'blob'
    }).subscribe(
      async res => {
        this.carrierConfirmationPdfPreviewLoading = false;

        const blob = res.body;
        const contentType = (res.headers.get('content-type') || blob?.type || '').toLowerCase();

        if (!blob || !contentType.includes('application/pdf')) {
          const message = await this.getBlobErrorMessage(blob, 'Unable to preview carrier confirmation PDF.');
          this.toastr.error(message);
          return;
        }

        this.carrierConfirmationPdfObjectUrl = URL.createObjectURL(blob);
        this.carrierConfirmationPdfPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.carrierConfirmationPdfObjectUrl);
        this.carrierConfirmationPdfDialogRef = this.dialog.open(this.carrierConfirmationPdfPreviewDialog, {
          width: 'min(96vw, 1100px)',
          maxWidth: '96vw',
          maxHeight: '94vh',
          panelClass: 'carrier-confirmation-preview-dialog',
          backdropClass: 'carrier-confirmation-preview-backdrop',
          autoFocus: false
        });

        this.carrierConfirmationPdfDialogRef.afterClosed()
          .pipe(takeUntil(this.destroy$))
          .subscribe(() => this.clearCarrierConfirmationPdfPreview());
      },
      async err => {
        this.carrierConfirmationPdfPreviewLoading = false;
        const message = await this.getBlobErrorMessage(err?.error, 'Unable to preview carrier confirmation PDF.');
        this.toastr.error(message);
      }
    );
  }

  sendCarrierConfirmationEmail(send_as_pdf: boolean){
    const postBody = this.buildCarrierConfirmationPostBody(send_as_pdf);
    const sendingFromPreviewDialog = !!this.carrierConfirmationPdfDialogRef;

    if (send_as_pdf || sendingFromPreviewDialog) {
      this.carrierConfirmationPdfSending = true;
    }

    this.http.post<any>(this.carrierConfirmationWorkflowUrl, postBody).subscribe(
      res => {
        console.log('API Success Response:', res);
        // Check for the API response code (404 in body) to trigger the modal
        if(res.code && res.code === 404){
          this.toastr.warning('Carrier email not found. Please enter manually.');
          this.carrierEmailAddressControl.reset(); // Reset to clear any previous manual attempts
          this.pdfToggle = send_as_pdf;
          this.carrierConfirmationPdfDialogRef?.close();
          this.dialog.open(this.carrierEmailAddressDialog);
        } else if (res.success === true || res.status === 'Email sent successfully!') {
          this.toastr.success('Carrier confirmation email sent!');
          this.optionalCCControl.reset(); // Clear optional fields on success
          this.optionalBCCControl.reset();
          if (send_as_pdf || sendingFromPreviewDialog) {
            this.carrierConfirmationPdfDialogRef?.close();
          }
        } else if (res.reason && res.reason === 'duplicate'){
          this.toastr.error('Carrier confirmation email already sent.');
        } else {
          // Handle other API-level errors (not a 404/modal-trigger)
          this.toastr.error(`Email failed: ${res.message || 'Unknown error'}`);
        }

        if (send_as_pdf || sendingFromPreviewDialog) {
          this.carrierConfirmationPdfSending = false;
        }
      },
      err => {
        console.error('HTTP Error:', err);
        this.toastr.error('A general error occurred while trying to send the email.');
        if (send_as_pdf || sendingFromPreviewDialog) {
          this.carrierConfirmationPdfSending = false;
        }
      }
    )
  }

  sendCarrierConfirmationEmailWithAddressInput(){
    // Validate the manual email input
    if (this.carrierEmailAddressControl.invalid) {
      this.carrierEmailAddressControl.markAsTouched();
      this.toastr.error('Please enter a valid carrier email address.');
      return;
    }
    
    const postBody = this.buildCarrierConfirmationPostBody(this.pdfToggle, {
      'carrier_email_address': this.carrierEmailAddressControl.value, // Manual email passed here
    });
    
    this.http.post<any>(this.carrierConfirmationWorkflowUrl, postBody).subscribe(
      res => {
        console.log(res);
        if (res.success === true || res.status === 'Email sent successfully!') {
          this.toastr.success('Carrier confirmation email sent!');
          this.closeCarrierEmailAddressDialog();
        } else {
          this.toastr.error(`Email failed: ${res.message || 'Unknown error'}`);
        }
      },
      err => {
        console.error(err);
        this.toastr.error('Error sending email.');
      }
    )
  }

  closeCarrierEmailAddressDialog(){
    // Clear the controls and close the dialog
    this.carrierEmailAddressControl.reset();
    this.optionalCCControl.reset();
    this.optionalBCCControl.reset();
    this.dialog.closeAll();
  }

  closeCarrierConfirmationPdfPreview(): void {
    this.carrierConfirmationPdfDialogRef?.close();
  }

  private clearCarrierConfirmationPdfPreview(): void {
    if (this.carrierConfirmationPdfObjectUrl) {
      URL.revokeObjectURL(this.carrierConfirmationPdfObjectUrl);
    }

    this.carrierConfirmationPdfObjectUrl = null;
    this.carrierConfirmationPdfPreviewUrl = null;
    this.carrierConfirmationPdfDialogRef = null;
    this.carrierConfirmationPdfSending = false;
  }

  private async getBlobErrorMessage(blob: Blob | null | undefined, fallback: string): Promise<string> {
    if (!blob) {
      return fallback;
    }

    try {
      const text = await blob.text();
      if (!text) {
        return fallback;
      }

      try {
        const parsed = JSON.parse(text);
        return parsed.message || parsed.error || fallback;
      } catch {
        return text.slice(0, 240);
      }
    } catch {
      return fallback;
    }
  }

  private findFscRateActiveOn(rows: FscRate[], date: string): FscRate | null {
    return rows
      .filter(row =>
        isActive(row.active) &&
        (!row.effective_start || row.effective_start.slice(0, 10) <= date) &&
        (!row.effective_end || row.effective_end.slice(0, 10) >= date)
      )
      .sort((a, b) =>
        (b.effective_start || '').localeCompare(a.effective_start || '')
      )[0] || null;
  }
}
